import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Cell, ReferenceLine,
} from "recharts";
import {
  FaCheck, FaTimes, FaExclamationTriangle, FaFileExport, FaUndo,
  FaLock, FaArrowRight, FaRegClock,
} from "react-icons/fa";

// ══════════════════════════════════════════════════════════════════════════
// ISO / MERCHANT DEMO DASHBOARD, v2
//
// WHAT CHANGED FROM v1, AND WHY
//
// 1. One fixture set, everything derived. v1 held 40+ independent constants
//    that did not reconcile (portfolio VAMP did not follow from the
//    chargeback counts; the case queue did not belong to the merchants
//    shown). Here MERCHANTS is the only source of truth and every figure
//    on screen is computed from it, so the page survives a prospect
//    asking "how does that number follow from that one".
//
// 2. The hero is the prospect's arithmetic, not our claim. The rep types
//    the prospect's volume, ticket, approval rate and chargeback count into
//    the page and it recomputes. Every assumption behind the output is an
//    editable field on screen, including the lift range and the hold rate.
//    Nothing here asserts what our own book does.
//
// 3. Removed: renewal rates, average tenure, NPS, named customers. We have
//    six programs in production and no year-two cohort, so those figures
//    could not be defended. The two real proof points are stated with their
//    scope attached, once.
//
// 4. Actions that change state. Agent action under a 4-eyes gate, rule
//    what-if with projected impact before commit, 3DS step-up, case
//    disposition, KYB refresh, decision-record export. Every one of them
//    writes to the consequence ledger, with PROJECTED kept visually
//    separate from REALIZED.
//
// 5. Demo labelling is legible and non-dismissible. The v1 chip was 10px
//    white at 0.18 opacity on a blue gradient, invisible on a projector.
//
// Data contract for Phase 2: replace buildPortfolio() with the analytics
// endpoints. Keep the derivation functions untouched, they are the same
// arithmetic the backend should return.
// ══════════════════════════════════════════════════════════════════════════

// ─── TOKENS ──────────────────────────────────────────────────────────────
const T = {
  ink: "#0B1220",
  body: "#2C3648",
  muted: "#6B7688",
  faint: "#9AA3B2",
  line: "#DFE3E9",
  paper: "#F4F5F8",
  card: "#FFFFFF",
  accent: "#1F4FD8",
  accentSoft: "#EDF1FE",
  good: "#0F8B62",
  warn: "#B4690E",
  bad: "#C0332B",
  goodSoft: "#E9F5F0",
  warnSoft: "#FBF2E4",
  badSoft: "#FBECEA",
};

const mono = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace";
const sans = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";
const num = { fontVariantNumeric: "tabular-nums", fontFeatureSettings: "'tnum'" };

// ─── NETWORK THRESHOLDS ──────────────────────────────────────────────────
// Verified Aug 13 2026 against Visa's April 2026 VAMP schedule + MRC, cside,
// basistheory and chargebacks911 secondary sources. Merchant tier has ONE
// enforcement threshold (Excessive) — no separately-published "Above-standard"
// merchant tier. The 90 bps floor is a monitoring/observation level only and
// is retained on the posture bar as a visual "watch" marker, not a fine tier.
// CEMEA carve-out remains 220 bps.
const VAMP = {
  aboveStandard: 90,   // MONITORING FLOOR (no fines) — used for the watch band on the posture bar
  excessive: 150,      // ENFORCEMENT — merchants at or above get $8/txn from Visa
  cemeaExcessive: 220, // CEMEA carve-out
  perTxnFineUsd: 8,
  verifiedOn: "Aug 13 2026 (Visa VAMP schedule effective April 1 2026)",
};

// ─── VERTICAL CONFIG ─────────────────────────────────────────────────────
// v1 was hardcoded to US iGaming. We sell into nutra, crypto on-ramp and
// cross-border too, so the vertical is a prop, not a layout.
const VERTICALS = {
  igaming: {
    label: "US regulated iGaming",
    mccNote: "MCC 7801 / 7995 / 7802",
    revenueTerm: "hold rate",
    holdDefault: 6.5,
    extras: ["Geo verification on every deposit", "Alt-method routing when issuers block the MCC", "Deposit-limit and cool-off flags respected on retry"],
  },
  nutra: {
    label: "Nutra / continuity",
    mccNote: "MCC 5122 / 5912",
    revenueTerm: "net margin",
    holdDefault: 38,
    extras: ["Rebill decline recovery", "Descriptor and CB-alert routing", "Cancellation-intent signals"],
  },
  cryptoramp: {
    label: "Crypto on-ramp / off-ramp",
    mccNote: "MCC 6051",
    revenueTerm: "take rate",
    holdDefault: 1.1,
    extras: ["Wallet screening pre-execution", "Hop-distance exposure", "Cross-rail pass-through detection"],
  },
  crossborder: {
    label: "Cross-border payments",
    mccNote: "MSB / PSP",
    revenueTerm: "take rate",
    holdDefault: 0.9,
    extras: ["Corridor-level risk scoring", "Sanctions screening pre-settlement", "Beneficiary reuse patterns"],
  },
};

// ─── FIXTURES ────────────────────────────────────────────────────────────
// Four archetypes. An all-green portfolio has no story, so two of these
// are in trouble. The breach-trajectory merchant is the demo.
const MERCHANTS = [
  {
    id: "northline",
    name: "Northline Sportsbook",
    segment: "Sportsbook",
    archetype: "Mature, healthy",
    liveSince: "Feb 2025",
    attempted: 64200, approved: 48150, avgTicket: 182,
    tc40: 260, disputes: 130,
    blocked: 3080, reviewed: 12970,
    threeDS: { frictionless: 38520, challengePassed: 6820, challengeFailed: 1180, bypass: 1630 },
    declines: [
      { code: "51", reason: "Insufficient funds", count: 6420, recovered: 3970 },
      { code: "05", reason: "Do Not Honor", count: 4180, recovered: 1890 },
      { code: "54", reason: "Expired card", count: 1240, recovered: 1040 },
      { code: "62", reason: "Restricted card", count: 890, recovered: 120 },
    ],
    cases: [
      { id: "C-4471", sev: "Medium", title: "Deposit velocity above cohort", age: "2d" },
      { id: "C-4468", sev: "Low", title: "Device reuse across two accounts", age: "4d" },
      { id: "C-4465", sev: "Low", title: "Name mismatch on instrument", age: "5d" },
    ],
    agent: {
      id: "AG-2201",
      finding: "Card-testing pattern on a single BIN, 41 accounts",
      proposal: "Add BIN 4173xx to the velocity rule at 3 attempts per hour",
      confidence: 0.91,
      evidence: [
        "41 accounts, 0 prior deposits, created within 90 minutes of first attempt",
        "Single BIN 4173xx, 214 attempts, 6 approvals, 4 already disputed",
        "Shared device fingerprint on 29 of 41 accounts",
        "No geo anomaly, so a blanket geo rule would not have caught this",
      ],
      projected: { approvalPP: -0.08, vampBps: -6, blockedTxns: 208 },
    },
    trend: [70.9, 71.4, 72.0, 72.8, 73.6, 74.2, 74.6, 75.0],
    vampTrend: [118, 109, 101, 96, 91, 86, 83, 81],
  },
  {
    id: "coastline",
    name: "Coastline Interactive",
    segment: "Sportsbook",
    archetype: "Drifting toward breach",
    liveSince: "Apr 2025",
    attempted: 30900, approved: 21050, avgTicket: 96,
    tc40: 240, disputes: 61,
    blocked: 1240, reviewed: 8610,
    threeDS: { frictionless: 14740, challengePassed: 3980, challengeFailed: 1420, bypass: 910 },
    declines: [
      { code: "51", reason: "Insufficient funds", count: 4210, recovered: 2380 },
      { code: "05", reason: "Do Not Honor", count: 3120, recovered: 1180 },
      { code: "54", reason: "Expired card", count: 720, recovered: 580 },
      { code: "62", reason: "Restricted card", count: 610, recovered: 70 },
    ],
    cases: [
      { id: "C-4480", sev: "Critical", title: "Dispute ratio 143 bps, trending to breach", age: "1d" },
      { id: "C-4477", sev: "High", title: "Affiliate cohort, 11x cohort dispute rate", age: "1d" },
      { id: "C-4474", sev: "High", title: "Chargeback cluster, one promo code", age: "3d" },
      { id: "C-4470", sev: "Medium", title: "Bonus abuse signature, 22 accounts", age: "3d" },
      { id: "C-4466", sev: "Medium", title: "Repeat first-party dispute filer", age: "6d" },
      { id: "C-4461", sev: "Medium", title: "Instrument shared across 4 accounts", age: "8d" },
    ],
    agent: {
      id: "AG-2214",
      finding: "One affiliate source drives 61% of disputes on 9% of volume",
      proposal: "Route affiliate 8841 traffic to 3DS force and hold first deposit 24h",
      confidence: 0.87,
      evidence: [
        "Affiliate 8841: 9.1% of attempted volume, 61.4% of disputes filed",
        "First-deposit dispute rate 4.2% vs 0.38% portfolio",
        "68% of that cohort never places a second deposit",
        "Blocking outright costs 2,810 legitimate deposits per month, so step-up is the cheaper control",
      ],
      projected: { approvalPP: -0.6, vampBps: -47, blockedTxns: 0 },
    },
    trend: [70.4, 70.1, 69.6, 69.3, 68.9, 68.5, 68.2, 68.1],
    vampTrend: [88, 96, 104, 112, 121, 130, 138, 143],
  },
  {
    id: "bluepine",
    name: "Bluepine Casino",
    segment: "Casino",
    archetype: "Newly boarded, ramping",
    liveSince: "3 weeks ago",
    attempted: 12400, approved: 8700, avgTicket: 143,
    tc40: 61, disputes: 18,
    blocked: 520, reviewed: 3180,
    threeDS: { frictionless: 6120, challengePassed: 1810, challengeFailed: 420, bypass: 350 },
    declines: [
      { code: "51", reason: "Insufficient funds", count: 1810, recovered: 780 },
      { code: "05", reason: "Do Not Honor", count: 1120, recovered: 340 },
      { code: "54", reason: "Expired card", count: 310, recovered: 210 },
      { code: "62", reason: "Restricted card", count: 240, recovered: 20 },
    ],
    cases: [
      { id: "C-4482", sev: "Low", title: "New-merchant baseline still forming", age: "1d" },
      { id: "C-4479", sev: "Low", title: "Two accounts, same funding instrument", age: "2d" },
    ],
    agent: {
      id: "AG-2219",
      finding: "Model still in observation, retry timing not yet tuned to this book",
      proposal: "Enable paycheck-window retry for code 51, cohort-calibrated",
      confidence: 0.64,
      evidence: [
        "Only 3 weeks of history, cohort baseline confidence 0.64",
        "Code 51 recovery running 43% vs 62% on comparable mature books",
        "Deposit-limit and cool-off flags present on 1.9% of accounts, excluded from retry",
        "Recommend committing after week 6, when the baseline stabilises",
      ],
      projected: { approvalPP: 1.9, vampBps: 2, blockedTxns: 0 },
    },
    trend: [null, null, null, null, 66.8, 68.4, 69.6, 70.2],
    vampTrend: [null, null, null, null, 104, 98, 93, 91],
  },
  {
    id: "vantage",
    name: "Vantage Racing",
    segment: "Racing",
    archetype: "Attack in progress",
    liveSince: "Sep 2025",
    attempted: 12450, approved: 8025, avgTicket: 210,
    tc40: 105, disputes: 28,
    blocked: 1420, reviewed: 3005,
    threeDS: { frictionless: 5210, challengePassed: 1640, challengeFailed: 880, bypass: 295 },
    declines: [
      { code: "51", reason: "Insufficient funds", count: 1620, recovered: 910 },
      { code: "05", reason: "Do Not Honor", count: 1480, recovered: 520 },
      { code: "54", reason: "Expired card", count: 290, recovered: 240 },
      { code: "62", reason: "Restricted card", count: 310, recovered: 40 },
    ],
    cases: [
      { id: "C-4484", sev: "Critical", title: "Enumeration attack, live, 1,240 attempts/hr", age: "40m" },
      { id: "C-4483", sev: "Critical", title: "Dispute ratio 166 bps, above excessive", age: "6h" },
      { id: "C-4481", sev: "High", title: "Stolen-credential cohort, 88 accounts", age: "1d" },
      { id: "C-4478", sev: "High", title: "Payout request before deposit clears", age: "1d" },
      { id: "C-4476", sev: "High", title: "Geo mismatch cluster, 34 accounts", age: "2d" },
      { id: "C-4473", sev: "High", title: "Instrument enumeration, sequential PANs", age: "2d" },
      { id: "C-4471b", sev: "Medium", title: "Refund-abuse signature", age: "4d" },
      { id: "C-4469", sev: "Medium", title: "Velocity spike, single IP block", age: "5d" },
      { id: "C-4464", sev: "Medium", title: "Repeat failed 3DS, same device", age: "7d" },
    ],
    agent: {
      id: "AG-2223",
      finding: "Live enumeration attack, sequential PANs, one ASN",
      proposal: "Block ASN 20473 at the edge and force 3DS on all new accounts for 24h",
      confidence: 0.96,
      evidence: [
        "1,240 attempts per hour from ASN 20473, baseline 18",
        "Sequential PAN pattern across 6 BINs, 0.7% approval rate on the cohort",
        "88 accounts created in 22 minutes, all single-instrument",
        "Estimated 214 disputes inbound if unblocked for another 12 hours",
      ],
      projected: { approvalPP: -0.4, vampBps: -38, blockedTxns: 1180 },
    },
    trend: [72.1, 71.6, 70.8, 69.4, 68.1, 66.9, 65.4, 64.5],
    vampTrend: [96, 104, 116, 128, 139, 149, 158, 166],
  },
];

const MONTHS = ["Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun"];

// ─── ISO CONFIG + BOARDING (Ticket 9: ISO audience view) ────────────────
// The ISO doesn't buy approval lift, it buys keeping merchants alive on the
// sponsor's book and boarding faster. This block feeds the ISO-audience
// sections that appear when the audience toggle is set to "iso".
const ISO_CONFIG = {
  residualBps: 12,          // ISO's residual on merchant processed volume, in bps
  sponsorBank: "Vantage Federal Sponsor Bank",
  target_ttl_days: 21,      // sponsor-agreed boarding SLA
};

const BOARDING = {
  live_last_30d: 1,
  live_last_60d: 2,
  live_last_90d: 3,
  median_ttl_days: 14,
  applications: [
    { stage: "Application",   count: 3 },
    { stage: "KYB / KYC",     count: 4 },
    { stage: "MATCH check",   count: 1 },
    { stage: "Underwriter",   count: 2 },
    { stage: "Sandbox",       count: 2 },
    { stage: "Live prep",     count: 1 },
  ],
  failure_rate_90d_pct: 5.9,
  bottleneck: "MATCH check — 1 stuck 6 days on manual review",
};

// The only two claims on this page about Idenza itself. Scope attached.
const PROOF = [
  { figure: "~60%", claim: "false-positive reduction", scope: "measured across 16 programs at one US sponsor bank" },
  { figure: "6", claim: "programs in production", scope: "as of this build" },
];

// ─── DERIVATION ──────────────────────────────────────────────────────────
// Every displayed figure comes from here. If a prospect asks where a number
// comes from, the answer is one of these functions.
const approvalPct = (m) => (m.approved / m.attempted) * 100;
const vampBps = (m) => ((m.tc40 + m.disputes) / m.approved) * 10000;
const volumeUsd = (m) => m.approved * m.avgTicket;
const declineCount = (m) => m.attempted - m.approved;
const recoveredCount = (m) => m.declines.reduce((s, d) => s + d.recovered, 0);
const recoveryPct = (m) => (recoveredCount(m) / m.declines.reduce((s, d) => s + d.count, 0)) * 100;
const blockRatePct = (m) => (m.blocked / m.attempted) * 100;
const threeDSTotal = (m) => Object.values(m.threeDS).reduce((a, b) => a + b, 0);
const frictionlessPct = (m) =>
  ((m.threeDS.frictionless + m.threeDS.bypass) / threeDSTotal(m)) * 100;
const openCases = (m) => m.cases.length;

const postureOf = (bps) =>
  bps >= VAMP.excessive ? "breach" : bps >= VAMP.aboveStandard ? "watch" : "ok";

const portfolio = (list) => {
  const attempted = list.reduce((s, m) => s + m.attempted, 0);
  const approved = list.reduce((s, m) => s + m.approved, 0);
  const tc40 = list.reduce((s, m) => s + m.tc40, 0);
  const disputes = list.reduce((s, m) => s + m.disputes, 0);
  const blocked = list.reduce((s, m) => s + m.blocked, 0);
  return {
    attempted, approved, blocked,
    volume: list.reduce((s, m) => s + volumeUsd(m), 0),
    approvalPct: (approved / attempted) * 100,
    vampBps: ((tc40 + disputes) / approved) * 10000,
    blockRatePct: (blocked / attempted) * 100,
    openCases: list.reduce((s, m) => s + openCases(m), 0),
    atRisk: list.filter((m) => postureOf(vampBps(m)) !== "ok").length,
  };
};

// ─── ISO audience derivations ────────────────────────────────────────────
// The ISO sells "your merchants stay alive on the sponsor's book, and you
// board more of them faster". Everything below rolls the MERCHANTS fixture
// into the shape an ISO ops lead reads.
const residualUsd = (m) => volumeUsd(m) * ISO_CONFIG.residualBps / 10000;

// Days-to-breach at current VAMP slope. Returns null if not on track to
// breach, else an integer number of days at the current bps/month rate.
const daysToBreach = (m) => {
  const current = vampBps(m);
  if (current >= VAMP.excessive) return 0;
  const vt = (m.vampTrend || []).filter((x) => x != null);
  if (vt.length < 3) return null;
  const recent = vt.slice(-3);
  const slopePerMonth = (recent[recent.length - 1] - recent[0]) / (recent.length - 1);
  if (slopePerMonth <= 0) return null;
  return Math.round(((VAMP.excessive - current) / slopePerMonth) * 30);
};

const isoMetrics = (list) => {
  const totalResidual = list.reduce((s, m) => s + residualUsd(m), 0);
  const atRisk = list.filter((m) => postureOf(vampBps(m)) !== "ok");
  const atRiskResidual = atRisk.reduce((s, m) => s + residualUsd(m), 0);
  const trending = list
    .map((m) => ({ m, current: vampBps(m), dtb: daysToBreach(m), residual: residualUsd(m) }))
    .filter((x) => x.current >= VAMP.aboveStandard || (x.dtb !== null && x.dtb <= 60))
    .sort((a, b) => (a.dtb ?? 9999) - (b.dtb ?? 9999));
  const boardingBacklog = BOARDING.applications.reduce((s, a) => s + a.count, 0);
  return {
    residualsMTD: totalResidual,
    residualsAnnualized: totalResidual * 12,
    atRiskResidual,
    atRiskCount: atRisk.length,
    trending,
    boardingBacklog,
  };
};

// Prospect projection. Every input is on screen and editable, which is the
// point: the output is their arithmetic, not our claim.
const project = (p) => {
  const approvedTxns = p.approvedVolume / p.avgTicket;
  const attemptedTxns = approvedTxns / (p.approvalPct / 100);
  const gainedTxns = attemptedTxns * (p.liftPP / 100);
  const recoveredVolume = gainedTxns * p.avgTicket;
  const grossRevenue = recoveredVolume * (p.holdRatePct / 100);
  const frictionVolume = recoveredVolume * (p.frictionPct / 100);
  const frictionRevenue = frictionVolume * (p.holdRatePct / 100);
  const cbAvoided = p.cbPerMonth * (p.cbReductionPct / 100);
  const cbSaving = cbAvoided * p.cbFee;
  return {
    attemptedTxns, approvedTxns, gainedTxns, recoveredVolume,
    grossRevenue, frictionVolume, frictionRevenue, cbAvoided, cbSaving,
    net: grossRevenue - frictionRevenue + cbSaving,
  };
};

// ─── FORMAT ──────────────────────────────────────────────────────────────
const f0 = (n) => (n == null ? "—" : Math.round(n).toLocaleString());
const f1 = (n) => (n == null ? "—" : n.toFixed(1));
const pct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);
const bps = (n) => (n == null ? "—" : `${Math.round(n)} bps`);
const usd = (n) =>
  n == null ? "—" :
  Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` :
  Math.abs(n) >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n)}`;
const usdExact = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString()}`);

// ─── PRIMITIVES ──────────────────────────────────────────────────────────
const Card = ({ children, style }) => (
  <div style={{
    background: T.card, border: `1px solid ${T.line}`, borderRadius: 6,
    ...style,
  }}>{children}</div>
);

const Head = ({ title, sub, right }) => (
  <div style={{
    padding: "12px 16px", borderBottom: `1px solid ${T.line}`,
    display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12,
  }}>
    <div>
      <div style={{ fontSize: 13, fontWeight: 650, color: T.ink, letterSpacing: "-0.01em" }}>{title}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
    </div>
    {right}
  </div>
);

const Eyebrow = ({ children }) => (
  <div style={{
    fontFamily: mono, fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase",
    color: T.faint, fontWeight: 600,
  }}>{children}</div>
);

const Stat = ({ label, value, note, tone }) => (
  <div style={{ padding: "12px 16px" }}>
    <Eyebrow>{label}</Eyebrow>
    <div style={{
      fontSize: 22, fontWeight: 600, marginTop: 5, letterSpacing: "-0.02em",
      color: tone === "bad" ? T.bad : tone === "warn" ? T.warn : tone === "good" ? T.good : T.ink,
      ...num,
    }}>{value}</div>
    {note && <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{note}</div>}
  </div>
);

const Pill = ({ tone = "muted", children }) => {
  const map = {
    ok: [T.goodSoft, T.good], watch: [T.warnSoft, T.warn], breach: [T.badSoft, T.bad],
    muted: [T.paper, T.muted], accent: [T.accentSoft, T.accent],
  };
  const [bg, fg] = map[tone] || map.muted;
  return (
    <span style={{
      background: bg, color: fg, fontSize: 10.5, fontWeight: 650, padding: "2.5px 7px",
      borderRadius: 3, whiteSpace: "nowrap", letterSpacing: "0.01em",
    }}>{children}</span>
  );
};

const Btn = ({ kind = "ghost", onClick, children, disabled, title }) => {
  const base = {
    fontFamily: sans, fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 5,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
    display: "inline-flex", alignItems: "center", gap: 6, transition: "background 120ms",
  };
  const kinds = {
    primary: { background: T.accent, color: "#FFF", border: `1px solid ${T.accent}` },
    good: { background: T.good, color: "#FFF", border: `1px solid ${T.good}` },
    danger: { background: T.card, color: T.bad, border: `1px solid ${T.line}` },
    ghost: { background: T.card, color: T.body, border: `1px solid ${T.line}` },
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{ ...base, ...kinds[kind] }}>{children}</button>
  );
};

// ─── DEMO BANNER ─────────────────────────────────────────────────────────
// Legible, non-dismissible, first thing on the page. This replaces the v1
// chip. The rep says this out loud in the first thirty seconds and then
// never has to defend a number's provenance again.
const DemoBanner = () => (
  <div style={{
    background: "#1B2436", color: "#E7EBF2", padding: "9px 16px", borderRadius: 6,
    display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap",
  }}>
    <span style={{
      fontFamily: mono, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700,
      background: "#F0B429", color: "#1B2436", padding: "3px 7px", borderRadius: 3,
    }}>DEMO TENANT</span>
    <span style={{ fontSize: 12, lineHeight: 1.4 }}>
      Synthetic merchants, seeded data, live product. Figures below reconcile with each
      other but describe no real customer. The two claims about Idenza's own results are
      marked and scoped.
    </span>
  </div>
);

// ─── PROSPECT MATH ───────────────────────────────────────────────────────
const Field = ({ label, value, onChange, prefix, suffix, step = 1, width = 96 }) => (
  <label style={{ display: "block" }}>
    <Eyebrow>{label}</Eyebrow>
    <div style={{
      display: "flex", alignItems: "center", gap: 3, marginTop: 5,
      border: `1px solid ${T.line}`, borderRadius: 5, padding: "5px 8px", background: T.card,
      width: "fit-content",
    }}>
      {prefix && <span style={{ color: T.faint, fontSize: 12 }}>{prefix}</span>}
      <input
        type="number" value={value} step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          border: "none", outline: "none", width, fontSize: 13.5, fontWeight: 600,
          color: T.ink, fontFamily: sans, background: "transparent", ...num,
        }}
      />
      {suffix && <span style={{ color: T.faint, fontSize: 12 }}>{suffix}</span>}
    </div>
  </label>
);

const ProspectMath = ({ p, set, vertical }) => {
  const r = project(p);
  const v = VERTICALS[vertical];
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Start with your numbers, not ours"
        sub="Type the prospect's current figures. Every assumption behind the output is a field you can change in front of them, which is the reason this holds up under questioning."
      />
      <div style={{ padding: "14px 16px", display: "flex", gap: 20, flexWrap: "wrap", borderBottom: `1px solid ${T.line}` }}>
        <Field label="Approved volume / mo" value={p.approvedVolume} prefix="$" step={100000} width={110}
          onChange={(n) => set({ ...p, approvedVolume: n })} />
        <Field label="Average ticket" value={p.avgTicket} prefix="$" width={64}
          onChange={(n) => set({ ...p, avgTicket: n })} />
        <Field label="Approval rate today" value={p.approvalPct} suffix="%" step={0.1} width={58}
          onChange={(n) => set({ ...p, approvalPct: n })} />
        <Field label="Chargebacks / mo" value={p.cbPerMonth} width={64}
          onChange={(n) => set({ ...p, cbPerMonth: n })} />
      </div>

      <div style={{ padding: "12px 16px", background: T.paper, borderBottom: `1px solid ${T.line}` }}>
        <Eyebrow>Assumptions, all editable, all visible</Eyebrow>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 8 }}>
          <Field label="Approval lift" value={p.liftPP} suffix="pp" step={0.1} width={48}
            onChange={(n) => set({ ...p, liftPP: n })} />
          <Field label={v.revenueTerm} value={p.holdRatePct} suffix="%" step={0.1} width={48}
            onChange={(n) => set({ ...p, holdRatePct: n })} />
          <Field label="Friction offset" value={p.frictionPct} suffix="%" width={48}
            onChange={(n) => set({ ...p, frictionPct: n })} />
          <Field label="CB reduction" value={p.cbReductionPct} suffix="%" width={48}
            onChange={(n) => set({ ...p, cbReductionPct: n })} />
          <Field label="Cost per CB" value={p.cbFee} prefix="$" width={48}
            onChange={(n) => set({ ...p, cbFee: n })} />
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 10, lineHeight: 1.5, maxWidth: 720 }}>
          Lift defaults to 3.5pp, the conservative end. Say the range out loud: 2 to 3pp on an
          already-tuned book, 8 to 10pp for a merchant new to CNP. Then offer the 90-day shadow
          pilot, because the only number that decides this is theirs.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))" }}>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Deposits recovered / mo" value={f0(r.gainedTxns)}
            note={`${usd(r.recoveredVolume)} of deposit volume`} />
        </div>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label={`Revenue at ${p.holdRatePct}% ${v.revenueTerm}`} value={usdExact(r.grossRevenue)}
            note="deposit volume is not revenue, so this is the honest line" tone="good" />
        </div>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Friction offset" value={`−${usdExact(r.frictionRevenue)}`}
            note="step-ups and false positives we add" tone="warn" />
        </div>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Chargeback saving" value={usdExact(r.cbSaving)}
            note={`${f0(r.cbAvoided)} fewer at $${p.cbFee} each`} tone="good" />
        </div>
        <div style={{ background: T.accentSoft }}>
          <Stat label="Net monthly impact" value={usdExact(r.net)} note="their inputs, their arithmetic" />
        </div>
      </div>
    </Card>
  );
};

// ─── PORTFOLIO TABLE ─────────────────────────────────────────────────────
const PortfolioTable = ({ list, selected, onSelect }) => {
  const th = {
    textAlign: "left", padding: "8px 12px", fontFamily: mono, fontSize: 9.5,
    letterSpacing: "0.1em", textTransform: "uppercase", color: T.faint, fontWeight: 600,
    borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap",
  };
  const td = { padding: "10px 12px", fontSize: 12.5, borderBottom: `1px solid ${T.line}`, color: T.body, ...num };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
        <thead>
          <tr>
            <th style={th}>Merchant</th>
            <th style={{ ...th, textAlign: "right" }}>Volume</th>
            <th style={{ ...th, textAlign: "right" }}>Approval</th>
            <th style={{ ...th, textAlign: "right" }}>VAMP</th>
            <th style={{ ...th, textAlign: "right" }}>Open cases</th>
            <th style={th}>State</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {list.map((m) => {
            const posture = postureOf(vampBps(m));
            const on = selected === m.id;
            return (
              <tr key={m.id} onClick={() => onSelect(m.id)}
                style={{ cursor: "pointer", background: on ? T.accentSoft : "transparent" }}>
                <td style={{ ...td, fontFamily: sans }}>
                  <div style={{ fontWeight: 650, color: T.ink }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {m.segment} · live {m.liveSince}
                  </div>
                </td>
                <td style={{ ...td, textAlign: "right" }}>{usd(volumeUsd(m))}</td>
                <td style={{ ...td, textAlign: "right" }}>{pct(approvalPct(m))}</td>
                <td style={{
                  ...td, textAlign: "right", fontWeight: 650,
                  color: posture === "breach" ? T.bad : posture === "watch" ? T.warn : T.body,
                }}>{bps(vampBps(m))}</td>
                <td style={{ ...td, textAlign: "right" }}>{openCases(m)}</td>
                <td style={td}><Pill tone={posture}>{m.archetype}</Pill></td>
                <td style={{ ...td, color: T.accent, fontSize: 11, fontFamily: sans, fontWeight: 600 }}>
                  {on ? "Open below" : "Inspect"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── POSTURE BAR ─────────────────────────────────────────────────────────
const PostureBar = ({ current, onAct }) => {
  const max = VAMP.excessive * 1.35;
  const posture = postureOf(current);
  const color = posture === "breach" ? T.bad : posture === "watch" ? T.warn : T.good;
  const at = (v) => `${Math.min(100, (v / max) * 100)}%`;
  return (
    <div style={{ padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Eyebrow>Visa VAMP, this merchant</Eyebrow>
        <span style={{ fontSize: 17, fontWeight: 650, color, ...num }}>{bps(current)}</span>
      </div>
      <div style={{ position: "relative", height: 8, background: T.paper, borderRadius: 4, marginTop: 10 }}>
        <div style={{
          position: "absolute", inset: 0, width: at(current), background: color, borderRadius: 4,
        }} />
        {[["Above standard", VAMP.aboveStandard], ["Excessive", VAMP.excessive]].map(([lbl, v]) => (
          <div key={lbl} style={{ position: "absolute", left: at(v), top: -3, bottom: -3, width: 1.5, background: T.ink, opacity: 0.55 }} />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: T.faint, marginTop: 6, fontFamily: mono }}>
        <span>0</span>
        <span>{VAMP.aboveStandard} above std</span>
        <span>{VAMP.excessive} excessive</span>
      </div>
      <div style={{ fontSize: 10.5, color: T.warn, marginTop: 8, fontFamily: mono }}>
        thresholds: {VAMP.verifiedOn}
      </div>
      {posture !== "ok" && (
        <div style={{
          marginTop: 12, padding: "10px 12px", background: posture === "breach" ? T.badSoft : T.warnSoft,
          borderRadius: 5, display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap",
        }}>
          <FaExclamationTriangle style={{ color: posture === "breach" ? T.bad : T.warn, marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 12, color: T.body, lineHeight: 1.5, flex: 1, minWidth: 200 }}>
            {posture === "breach"
              ? "Above the excessive threshold now. Enforcement action available below."
              : "Trending toward excessive. Two controls below pull it back without touching approval materially."}
          </div>
          <Btn kind="primary" onClick={onAct}>Review recommended action <FaArrowRight size={10} /></Btn>
        </div>
      )}
    </div>
  );
};

// ─── AGENT ACTION, 4-EYES ────────────────────────────────────────────────
const AgentAction = ({ m, state, onDecide }) => {
  const a = m.agent;
  const decided = state.decision;
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Agent finding, awaiting human approval"
        sub="The pipeline detected, enriched and analysed on its own. It cannot act. A named human approves or rejects, and the rejection reason feeds the retune."
        right={<Pill tone="accent">{a.id} · confidence {a.confidence.toFixed(2)}</Pill>}
      />
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: 13.5, fontWeight: 650, color: T.ink, marginBottom: 4 }}>{a.finding}</div>
        <div style={{ fontSize: 12.5, color: T.body, marginBottom: 14 }}>
          Proposes: {a.proposal}
        </div>

        <Eyebrow>Evidence chain</Eyebrow>
        <ol style={{ margin: "8px 0 0", paddingLeft: 0, listStyle: "none" }}>
          {a.evidence.map((e, i) => (
            <li key={i} style={{
              display: "flex", gap: 10, padding: "7px 0",
              borderTop: i ? `1px solid ${T.line}` : "none", fontSize: 12.5, color: T.body, lineHeight: 1.5,
            }}>
              <span style={{ fontFamily: mono, fontSize: 10.5, color: T.faint, paddingTop: 2 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{e}</span>
            </li>
          ))}
        </ol>

        <div style={{
          marginTop: 14, padding: "10px 12px", background: T.paper, borderRadius: 5,
          display: "flex", gap: 22, flexWrap: "wrap",
        }}>
          <div>
            <Eyebrow>Projected approval</Eyebrow>
            <div style={{ fontSize: 15, fontWeight: 650, color: a.projected.approvalPP < 0 ? T.warn : T.good, ...num }}>
              {a.projected.approvalPP > 0 ? "+" : ""}{f1(a.projected.approvalPP)} pp
            </div>
          </div>
          <div>
            <Eyebrow>Projected VAMP</Eyebrow>
            <div style={{ fontSize: 15, fontWeight: 650, color: a.projected.vampBps < 0 ? T.good : T.warn, ...num }}>
              {a.projected.vampBps > 0 ? "+" : ""}{a.projected.vampBps} bps
            </div>
          </div>
          <div>
            <Eyebrow>Transactions affected</Eyebrow>
            <div style={{ fontSize: 15, fontWeight: 650, color: T.ink, ...num }}>{f0(a.projected.blockedTxns)}</div>
          </div>
          <Pill tone="warn">projected, not realised</Pill>
        </div>

        {!decided ? (
          <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Btn kind="good" onClick={() => onDecide("approved")}><FaCheck size={11} /> Approve as second pair of eyes</Btn>
            <Btn kind="danger" onClick={() => onDecide("rejected")}><FaTimes size={11} /> Reject and retune</Btn>
            <span style={{ fontSize: 11.5, color: T.muted, display: "inline-flex", alignItems: "center", gap: 5 }}>
              <FaLock size={10} /> signed with the approver's identity, written to the decision record
            </span>
          </div>
        ) : (
          <div style={{
            marginTop: 14, padding: "10px 12px", borderRadius: 5,
            background: decided === "approved" ? T.goodSoft : T.paper,
            fontSize: 12.5, color: T.body, lineHeight: 1.5,
          }}>
            {decided === "approved"
              ? `Approved and enforced. Rule weight retuned, ${a.id} closed. Effect moves from projected to realised at the next scoring window.`
              : `Rejected. The reason is now a training signal: the agent lowers confidence on this pattern class and re-presents with different evidence if it recurs.`}
          </div>
        )}
      </div>
    </Card>
  );
};

// ─── RULE WHAT-IF ────────────────────────────────────────────────────────
const RuleWhatIf = ({ m, threshold, setThreshold, committed, onCommit }) => {
  const base = 700;
  const delta = base - threshold;              // positive = stricter
  const approvalDelta = -(delta / 10) * 0.15;
  const vampDelta = -(delta / 10) * 2.4;
  const blockedDelta = (delta / 10) * 62;
  const dirty = threshold !== committed;
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Change a rule and see the cost before you commit"
        sub="This is the part nobody can screenshot back at you. Drag it, read the trade, then commit or discard."
        right={dirty ? <Pill tone="warn">uncommitted</Pill> : <Pill tone="ok">committed at {committed}</Pill>}
      />
      <div style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Block threshold, risk score</Eyebrow>
          <span style={{ fontFamily: mono, fontSize: 15, fontWeight: 700, color: T.ink, ...num }}>{threshold}</span>
        </div>
        <input
          type="range" min={560} max={840} step={10} value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          style={{ width: "100%", marginTop: 10, accentColor: T.accent }}
          aria-label="Block threshold"
        />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: T.faint, fontFamily: mono }}>
          <span>560 stricter</span><span>700 current</span><span>840 looser</span>
        </div>

        <div style={{
          marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 1, background: T.line, borderRadius: 5, overflow: "hidden",
        }}>
          {[
            ["Approval", `${approvalDelta > 0 ? "+" : ""}${f1(approvalDelta)} pp`, approvalDelta < 0 ? T.warn : T.good],
            ["VAMP", `${vampDelta > 0 ? "+" : ""}${Math.round(vampDelta)} bps`, vampDelta < 0 ? T.good : T.warn],
            ["Extra blocks", f0(Math.abs(blockedDelta)), T.body],
            ["Revenue effect", usd(approvalDelta / 100 * m.attempted * m.avgTicket), approvalDelta < 0 ? T.warn : T.good],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: T.card, padding: "10px 12px" }}>
              <Eyebrow>{l}</Eyebrow>
              <div style={{ fontSize: 15, fontWeight: 650, color: c, marginTop: 4, ...num }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Btn kind="primary" disabled={!dirty} onClick={() => onCommit(threshold)}>Commit to sandbox</Btn>
          <Btn disabled={!dirty} onClick={() => setThreshold(committed)}><FaUndo size={10} /> Discard</Btn>
          <span style={{ fontSize: 11.5, color: T.muted }}>
            Sandbox first, then the approval workflow. Nothing here reaches production on one click.
          </span>
        </div>
      </div>
    </Card>
  );
};

// ─── 3DS STEP-UP ON SEGMENT (senior brief, section D) ───────────────────
// Merchant-drilldown action: pick a segment (BIN / country / affiliate),
// see the projected friction cost, commit. Reads from the same numbers
// the drilldown shows — the friction cost is derived, not typed.
const SEGMENTS = [
  { key: "bin_4147",       label: "BIN 4147** (issuer cluster)", coveragePct: 6.8,  fpImpactPct: 1.2 },
  { key: "geo_ca",         label: "Geo — California",             coveragePct: 11.4, fpImpactPct: 0.6 },
  { key: "affiliate_8841", label: "Affiliate 8841",               coveragePct: 9.1,  fpImpactPct: 2.4 },
  { key: "first_deposit",  label: "First-deposit cohort",         coveragePct: 22.0, fpImpactPct: 0.8 },
];

const ThreeDsStepUp = ({ m, state, onCommit }) => {
  const [segKey, setSegKey] = useState(SEGMENTS[0].key);
  const seg = SEGMENTS.find((s) => s.key === segKey);
  const committed = state.committed || null;
  const attemptedInSegment = Math.round(m.attempted * seg.coveragePct / 100);
  const challengeAdds = Math.round(attemptedInSegment * 0.62);
  const abandonEst = Math.round(challengeAdds * 0.11);
  const frictionUsd = Math.round(abandonEst * m.avgTicket * 0.65);
  const vampReliefBps = -Math.round((seg.fpImpactPct / seg.coveragePct) * 18);
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Force 3DS step-up on a segment"
        sub="Move friction only where it earns its keep. Pick a segment, read the cost, then commit under 4-eyes."
        right={committed ? <Pill tone="ok">committed on {SEGMENTS.find((s) => s.key === committed)?.label}</Pill> : <Pill tone="warn">no segment enforced</Pill>}
      />
      <div style={{ padding: "12px 16px 4px" }}>
        <Eyebrow>Segment</Eyebrow>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
          {SEGMENTS.map((s) => (
            <Btn key={s.key} kind={s.key === segKey ? "primary" : "ghost"} onClick={() => setSegKey(s.key)}>
              {s.label}
            </Btn>
          ))}
        </div>
      </div>
      <div style={{
        margin: "12px 16px 0", padding: "10px 12px", background: T.paper, borderRadius: 5,
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 1, overflow: "hidden",
      }}>
        <div style={{ background: T.card, padding: "8px 12px" }}>
          <Eyebrow>Segment coverage</Eyebrow>
          <div style={{ fontSize: 15, fontWeight: 650, color: T.ink, marginTop: 4, ...num }}>{pct(seg.coveragePct)}</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{f0(attemptedInSegment)} txns/mo</div>
        </div>
        <div style={{ background: T.card, padding: "8px 12px" }}>
          <Eyebrow>Extra challenges</Eyebrow>
          <div style={{ fontSize: 15, fontWeight: 650, color: T.warn, marginTop: 4, ...num }}>+{f0(challengeAdds)}</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>~62% of segment</div>
        </div>
        <div style={{ background: T.card, padding: "8px 12px" }}>
          <Eyebrow>Est. abandon</Eyebrow>
          <div style={{ fontSize: 15, fontWeight: 650, color: T.warn, marginTop: 4, ...num }}>{f0(abandonEst)}</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>~11% of challenges</div>
        </div>
        <div style={{ background: T.card, padding: "8px 12px" }}>
          <Eyebrow>Friction cost</Eyebrow>
          <div style={{ fontSize: 15, fontWeight: 650, color: T.warn, marginTop: 4, ...num }}>{usd(-frictionUsd)}</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>abandon × ticket × 65%</div>
        </div>
        <div style={{ background: T.card, padding: "8px 12px" }}>
          <Eyebrow>Projected VAMP</Eyebrow>
          <div style={{ fontSize: 15, fontWeight: 650, color: T.good, marginTop: 4, ...num }}>{vampReliefBps} bps</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>2 scoring windows</div>
        </div>
      </div>
      <div style={{ padding: "12px 16px 14px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Btn kind="primary" disabled={committed === segKey}
          onClick={() => onCommit(seg, { frictionUsd, vampReliefBps, challengeAdds })}>
          {committed === segKey ? "Committed on this segment" : "Commit step-up under 4-eyes"}
        </Btn>
        <span style={{ fontSize: 11.5, color: T.muted }}>
          Sandbox first, then approval. Idempotent — re-committing the same segment is a no-op.
        </span>
      </div>
    </Card>
  );
};

// ─── PER-MERCHANT KYB REFRESH (senior brief, section D) ─────────────────
const MerchantKybRefresh = ({ m, pending, onRefresh }) => (
  <Card style={{ marginBottom: 14 }}>
    <Head
      title="KYB / KYC freshness"
      sub={`Sponsor policy: refresh on 90-day cadence, or on any material control-change event.`}
      right={pending ? <Pill tone="warn">refresh pending</Pill> : <Pill tone="ok">current</Pill>}
    />
    <div style={{ padding: "14px 16px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 12.5, color: T.body, lineHeight: 1.5 }}>
          Runs Mesh + TheKYB in parallel. Results return within 24–48h and clear the overdue flag automatically. Adverse-media hits open a case.
        </div>
      </div>
      <Btn kind="primary" disabled={pending} onClick={() => onRefresh(m)}>
        {pending ? "Refresh queued" : "Trigger KYB refresh"}
      </Btn>
    </div>
  </Card>
);

// ─── CASE DISPOSITION DIALOG (senior brief, section D) ──────────────────
const DISPOSITION_REASONS = [
  "Confirmed benign — false positive",
  "Confirmed fraud — action taken elsewhere",
  "Merchant self-cleared with evidence",
  "Escalated to Idenza ops",
  "Insufficient evidence — monitor",
  "Duplicate of another case",
];

const DispositionDialog = ({ open, caseRow, onCancel, onConfirm }) => {
  const [reason, setReason] = useState(DISPOSITION_REASONS[0]);
  const [note, setNote] = useState("");
  const [attributor, setAttributor] = useState("demo.rep");
  if (!open || !caseRow) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(11,18,32,0.55)", zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}
      onClick={onCancel}
    >
      <div style={{
        background: T.card, border: `1px solid ${T.line}`, borderRadius: 8,
        maxWidth: 520, width: "100%", padding: 0, boxShadow: "0 24px 60px rgba(11,18,32,0.4)",
      }}
        onClick={(e) => e.stopPropagation()}
      >
        <Head title={`Disposition ${caseRow.id}`} sub={`${caseRow.sev} · ${caseRow.title}`} />
        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={{ display: "block" }}>
            <Eyebrow>Reason</Eyebrow>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              style={{
                marginTop: 6, width: "100%", padding: "8px 10px", fontSize: 13,
                border: `1px solid ${T.line}`, borderRadius: 5, background: T.card, color: T.ink, fontFamily: sans,
              }}>
              {DISPOSITION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label style={{ display: "block" }}>
            <Eyebrow>Note (optional, evidence link or comment)</Eyebrow>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Paste evidence link or short comment for the audit trail"
              style={{
                marginTop: 6, width: "100%", padding: "8px 10px", fontSize: 13,
                border: `1px solid ${T.line}`, borderRadius: 5, background: T.card, color: T.ink,
                fontFamily: sans, resize: "vertical",
              }} />
          </label>
          <label style={{ display: "block" }}>
            <Eyebrow>Attributor</Eyebrow>
            <input value={attributor} onChange={(e) => setAttributor(e.target.value)}
              style={{
                marginTop: 6, width: "100%", padding: "8px 10px", fontSize: 13,
                border: `1px solid ${T.line}`, borderRadius: 5, background: T.card, color: T.ink, fontFamily: sans,
              }} />
          </label>
        </div>
        <div style={{
          padding: "12px 18px", borderTop: `1px solid ${T.line}`, display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <Btn onClick={onCancel}>Cancel</Btn>
          <Btn kind="good" onClick={() => onConfirm({ reason, note, attributor })}>
            <FaCheck size={11} /> Confirm disposition
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ─── CASE QUEUE ──────────────────────────────────────────────────────────
const SEV_TONE = { Critical: "breach", High: "breach", Medium: "watch", Low: "muted" };

const CaseQueue = ({ cases, closed, onOpenDisposition, onExport }) => (
  <Card style={{ marginBottom: 14 }}>
    <Head
      title="Open cases"
      sub="Counts on this page are the sum of these rows. Disposition captures a reason + attributor, then the portfolio number moves."
      right={<Pill tone="muted">{cases.length - closed.length} open</Pill>}
    />
    <div>
      {cases.map((c) => {
        const done = closed.includes(c.id);
        return (
          <div key={c.id} style={{
            padding: "10px 16px", borderBottom: `1px solid ${T.line}`, display: "flex",
            alignItems: "center", gap: 12, opacity: done ? 0.45 : 1, flexWrap: "wrap",
          }}>
            <Pill tone={SEV_TONE[c.sev]}>{c.sev}</Pill>
            <span style={{ fontFamily: mono, fontSize: 11, color: T.faint }}>{c.id}</span>
            <span style={{ fontSize: 12.5, color: T.body, flex: 1, minWidth: 180, textDecoration: done ? "line-through" : "none" }}>
              {c.title}
            </span>
            <span style={{ fontSize: 11, color: T.faint, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <FaRegClock size={9} /> {c.age}
            </span>
            <Btn onClick={() => onExport(c)} title="Downloads examiner-defensible JSON artifact">
              <FaFileExport size={10} /> Record
            </Btn>
            <Btn kind={done ? "ghost" : "good"} disabled={done} onClick={() => onOpenDisposition(c)}>
              {done ? "Closed" : "Disposition"}
            </Btn>
          </div>
        );
      })}
    </div>
  </Card>
);

// ─── CHARTS ──────────────────────────────────────────────────────────────
const TwoUpCharts = ({ m }) => {
  const rows = MONTHS.map((mo, i) => ({
    month: mo, approval: m.trend[i], vamp: m.vampTrend[i],
  }));
  const axis = { stroke: T.faint, fontSize: 10.5, fontFamily: mono };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginBottom: 14 }}>
      <Card>
        <Head title="Approval rate" sub="This merchant, monthly. Derived from the same counts as the table above." />
        <div style={{ padding: "12px 8px 8px" }}>
          <ResponsiveContainer width="100%" height={168}>
            <LineChart data={rows} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke={T.line} vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: T.line }} tick={axis} />
              <YAxis domain={[60, 80]} tickLine={false} axisLine={false} tick={axis} unit="%" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 5, border: `1px solid ${T.line}` }} />
              <Line type="monotone" dataKey="approval" stroke={T.accent} strokeWidth={2} dot={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card>
        <Head title="VAMP trajectory" sub="Direction is the story a sponsor bank reads, not the level." />
        <div style={{ padding: "12px 8px 8px" }}>
          <ResponsiveContainer width="100%" height={168}>
            <LineChart data={rows} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke={T.line} vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: T.line }} tick={axis} />
              <YAxis domain={[60, 180]} tickLine={false} axisLine={false} tick={axis} />
              <ReferenceLine y={VAMP.excessive} stroke={T.bad} strokeDasharray="4 3"
                label={{ value: "excessive", fontSize: 10, fill: T.bad, position: "insideTopRight" }} />
              <ReferenceLine y={VAMP.aboveStandard} stroke={T.warn} strokeDasharray="4 3" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 5, border: `1px solid ${T.line}` }} />
              <Line type="monotone" dataKey="vamp" stroke={T.ink} strokeWidth={2} dot={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

const DeclineRecovery = ({ m }) => {
  const rows = m.declines.map((d) => ({
    name: `${d.code} ${d.reason}`,
    pct: (d.recovered / d.count) * 100,
    recovered: d.recovered,
    count: d.count,
  }));
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Soft-decline recovery"
        sub={`${f0(recoveredCount(m))} of ${f0(m.declines.reduce((s, d) => s + d.count, 0))} soft declines recaptured, ${pct(recoveryPct(m))} overall. Retry skips accounts flagged for deposit limits or cool-off, which is the first thing a licensed operator's compliance lead will ask.`}
      />
      <div style={{ padding: "12px 8px 8px" }}>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 40, left: 110, bottom: 0 }}>
            <XAxis type="number" domain={[0, 100]} hide />
            <YAxis type="category" dataKey="name" tickLine={false} axisLine={false}
              tick={{ fontSize: 11, fill: T.body, fontFamily: sans }} width={108} />
            <Tooltip formatter={(v) => `${v.toFixed(1)}%`}
              contentStyle={{ fontSize: 12, borderRadius: 5, border: `1px solid ${T.line}` }} />
            <Bar dataKey="pct" radius={[0, 3, 3, 0]} barSize={16}>
              {rows.map((r, i) => (
                <Cell key={i} fill={r.pct > 55 ? T.good : r.pct > 30 ? T.warn : T.bad} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// ─── CONSEQUENCE LEDGER ──────────────────────────────────────────────────
// The signature element. Everything the rep does in the meeting lands here,
// projected kept separate from realised. This is what the prospect
// remembers, and it is the one part of the page a competitor cannot
// screenshot back at us.
const Ledger = ({ entries, onReset }) => (
  <Card style={{ position: "sticky", top: 16 }}>
    <Head
      title="What you just did"
      sub="Session log. Projected effects stay labelled until a scoring window realises them."
      right={<Btn onClick={onReset}><FaUndo size={10} /> Reset</Btn>}
    />
    {entries.length === 0 ? (
      <div style={{ padding: "20px 16px", fontSize: 12.5, color: T.muted, lineHeight: 1.55 }}>
        Nothing yet. Take one action on the left, approve an agent finding or commit a
        threshold, and it records here with its projected effect. Running the meeting from
        this column is the whole point: a dashboard reports, a product does something.
      </div>
    ) : (
      <div style={{ maxHeight: 520, overflowY: "auto" }}>
        {entries.map((e, i) => (
          <div key={i} style={{ padding: "11px 16px", borderBottom: `1px solid ${T.line}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontSize: 12.5, fontWeight: 650, color: T.ink }}>{e.title}</span>
              <Pill tone={e.kind === "realised" ? "ok" : e.kind === "rejected" ? "muted" : "warn"}>
                {e.kind}
              </Pill>
            </div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>{e.detail}</div>
            <div style={{ fontFamily: mono, fontSize: 10, color: T.faint, marginTop: 5 }}>
              {e.at} · {e.merchant} · approver {e.by}
            </div>
          </div>
        ))}
      </div>
    )}
  </Card>
);

// ─── PROOF BAND ──────────────────────────────────────────────────────────
const ProofBand = () => (
  <Card style={{ marginBottom: 14 }}>
    <Head title="The only two claims here about Idenza's own results"
      sub="Both carry their scope. Everything else on this page describes the demo tenant." />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
      {PROOF.map((p, i) => (
        <div key={p.claim} style={{ padding: "14px 16px", borderLeft: i ? `1px solid ${T.line}` : "none" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 600, color: T.ink, letterSpacing: "-0.02em", ...num }}>{p.figure}</span>
            <span style={{ fontSize: 13, color: T.body, fontWeight: 600 }}>{p.claim}</span>
          </div>
          <div style={{ fontSize: 11.5, color: T.muted, marginTop: 5, lineHeight: 1.5 }}>{p.scope}</div>
        </div>
      ))}
    </div>
  </Card>
);

// ─── ISO AUDIENCE VIEW COMPONENTS (Ticket 9) ────────────────────────────
// Replaces ProspectMath at the top of the page when audience === "iso".
// Reason: an ISO reads residuals, at-risk exposure, and boarding, not the
// prospect's math.

const IsoTopStrip = ({ iso }) => (
  <Card style={{ marginBottom: 14 }}>
    <Head
      title="Your residual book, right now"
      sub={`Residuals at ${ISO_CONFIG.residualBps} bps of processed volume, sponsored by ${ISO_CONFIG.sponsorBank}. Numbers derive from the portfolio table; nothing shown here is a separate figure.`}
    />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
      <div style={{ borderRight: `1px solid ${T.line}` }}>
        <Stat label="Residuals MTD" value={usd(iso.residualsMTD)}
          note={`${MERCHANTS.length} merchants · ${ISO_CONFIG.residualBps} bps`} />
      </div>
      <div style={{ borderRight: `1px solid ${T.line}` }}>
        <Stat label="Annualised run-rate" value={usd(iso.residualsAnnualized)}
          note="portfolio × 12, straight-line" />
      </div>
      <div style={{ borderRight: `1px solid ${T.line}` }}>
        <Stat label="At-risk residual" value={usd(iso.atRiskResidual)}
          note={`${iso.atRiskCount} merchant${iso.atRiskCount === 1 ? "" : "s"} above standard`}
          tone={iso.atRiskCount ? "warn" : "good"} />
      </div>
      <div style={{ borderRight: `1px solid ${T.line}` }}>
        <Stat label="% of book at risk" value={pct((iso.atRiskResidual / iso.residualsMTD) * 100)}
          note="if these merchants get pulled off the sponsor"
          tone={iso.atRiskResidual > 0 ? "warn" : "good"} />
      </div>
      <div>
        <Stat label="Boarding backlog" value={f0(iso.boardingBacklog)}
          note="applications across all stages" />
      </div>
    </div>
  </Card>
);

// Watchlist — merchants trending to breach, with the ISO save action.
// Now with bulk actions (notify sponsor / tighten velocity / bulk KYB
// refresh) and a per-row Escalate button. Every action lands in the ledger.
const IsoWatchlist = ({
  trending, onOpen, onNotifySponsor, onTightenVelocity, onBulkKyb, onEscalate,
  sponsorNotified, velocityTightened, kybPending,
}) => {
  const th = {
    textAlign: "left", padding: "8px 12px", fontFamily: mono, fontSize: 9.5,
    letterSpacing: "0.1em", textTransform: "uppercase", color: T.faint, fontWeight: 600,
    borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap",
  };
  const td = { padding: "10px 12px", fontSize: 12.5, borderBottom: `1px solid ${T.line}`, color: T.body, ...num };
  const anyPending = trending.some((t) => kybPending.includes(t.m.id));
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Merchants trending to breach"
        sub="VAMP watchlist ranked by days to Excessive at current slope. Save-the-merchant work happens here."
        right={<Pill tone={trending.length ? "warn" : "ok"}>{trending.length} on watch</Pill>}
      />

      {trending.length > 0 && (
        <div style={{
          padding: "10px 12px", background: T.paper, borderBottom: `1px solid ${T.line}`,
          display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
        }}>
          <Eyebrow>Bulk actions</Eyebrow>
          <Btn kind={sponsorNotified ? "ghost" : "primary"} disabled={sponsorNotified} onClick={onNotifySponsor}>
            {sponsorNotified ? "Sponsor notified" : "Notify sponsor of at-risk book"}
          </Btn>
          <Btn kind={velocityTightened ? "ghost" : "ghost"} disabled={velocityTightened} onClick={onTightenVelocity}>
            {velocityTightened ? "Velocity tightened" : "Tighten velocity across watchlist"}
          </Btn>
          <Btn kind="ghost" disabled={anyPending && trending.every((t) => kybPending.includes(t.m.id))} onClick={onBulkKyb}>
            {anyPending && trending.every((t) => kybPending.includes(t.m.id))
              ? "KYB refresh queued"
              : "Bulk KYB refresh"}
          </Btn>
          <span style={{ fontSize: 11.5, color: T.muted, marginLeft: 6 }}>
            Every action writes to the ledger with projected impact before it lands.
          </span>
        </div>
      )}

      {trending.length === 0 ? (
        <div style={{ padding: "22px 16px", fontSize: 12.5, color: T.muted }}>
          Nobody on the watchlist. The book is clean this hour.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr>
                <th style={th}>Merchant</th>
                <th style={{ ...th, textAlign: "right" }}>VAMP now</th>
                <th style={{ ...th, textAlign: "right" }}>Days to breach</th>
                <th style={{ ...th, textAlign: "right" }}>Residual at risk</th>
                <th style={th}>Recommended</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {trending.map(({ m, current, dtb, residual }) => {
                const posture = postureOf(current);
                const kybOnThis = kybPending.includes(m.id);
                return (
                  <tr key={m.id}>
                    <td style={{ ...td, fontFamily: sans }}>
                      <div style={{ fontWeight: 650, color: T.ink, display: "flex", gap: 6, alignItems: "center" }}>
                        {m.name}
                        {kybOnThis && <Pill tone="warn">KYB pending</Pill>}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{m.archetype}</div>
                    </td>
                    <td style={{
                      ...td, textAlign: "right", fontWeight: 650,
                      color: posture === "breach" ? T.bad : T.warn,
                    }}>{bps(current)}</td>
                    <td style={{
                      ...td, textAlign: "right", fontWeight: 650,
                      color: dtb === null ? T.muted : dtb <= 14 ? T.bad : dtb <= 45 ? T.warn : T.body,
                    }}>{dtb === null ? "—" : dtb === 0 ? "breached" : `${dtb}d`}</td>
                    <td style={{ ...td, textAlign: "right" }}>{usd(residual)}</td>
                    <td style={{ ...td, fontFamily: sans, fontSize: 11.5, color: T.body }}>
                      {m.agent ? m.agent.proposal : "Review controls"}
                    </td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <Btn onClick={() => onEscalate(m)} title="Page Idenza risk lead">Escalate</Btn>{" "}
                      <Btn kind="primary" onClick={() => onOpen(m.id)}>Open <FaArrowRight size={10} /></Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// Boarding throughput — the second thing an ISO buys. Now with header
// actions (pause new / tighten underwriting), per-stage escalate/cancel,
// and an actionable bottleneck banner.
const IsoBoardingPanel = ({
  onEscalateStage, onCancelStageApp, onTogglePause, onToggleTighten,
  isPaused, isTightened, escalatedStages, cancelledApps,
}) => {
  const totalInFlight = BOARDING.applications.reduce((s, a) => s + a.count, 0);
  const th = {
    textAlign: "left", padding: "8px 12px", fontFamily: mono, fontSize: 9.5,
    letterSpacing: "0.1em", textTransform: "uppercase", color: T.faint, fontWeight: 600,
    borderBottom: `1px solid ${T.line}`,
  };
  const td = { padding: "9px 12px", fontSize: 12.5, borderBottom: `1px solid ${T.line}`, color: T.body, ...num };
  const cancelledCount = cancelledApps.length;
  return (
    <Card style={{ marginBottom: 14 }}>
      <Head
        title="Boarding throughput"
        sub={`Sponsor SLA: ${ISO_CONFIG.target_ttl_days} days from application to first live txn. This is the second thing the ISO buys.`}
        right={
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Btn kind={isTightened ? "primary" : "ghost"} onClick={onToggleTighten}
              title="Raise business-credit + sanctions look-back">
              {isTightened ? "Underwriting tightened" : "Tighten underwriting"}
            </Btn>
            <Btn kind={isPaused ? "danger" : "ghost"} onClick={onTogglePause}
              title="Safety switch: no new applications accepted">
              {isPaused ? "Boarding paused · Resume" : "Pause new applications"}
            </Btn>
          </div>
        }
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Live last 30d" value={f0(BOARDING.live_last_30d)} note="new merchants boarded" />
        </div>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Live last 90d" value={f0(BOARDING.live_last_90d)} note="trailing 3 months" />
        </div>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Median time-to-live"
            value={`${BOARDING.median_ttl_days}d`}
            note={`sponsor SLA ${ISO_CONFIG.target_ttl_days}d`}
            tone={BOARDING.median_ttl_days <= ISO_CONFIG.target_ttl_days ? "good" : "warn"} />
        </div>
        <div style={{ borderRight: `1px solid ${T.line}` }}>
          <Stat label="Applications in flight" value={f0(totalInFlight - cancelledCount)}
            note={cancelledCount > 0 ? `${cancelledCount} cancelled this session` : "across all stages"} />
        </div>
        <div>
          <Stat label="Boarding failure 90d" value={pct(BOARDING.failure_rate_90d_pct)}
            note="MATCH, KYB, or credit failed"
            tone={BOARDING.failure_rate_90d_pct > 10 ? "warn" : "good"} />
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${T.line}` }}>
        <div style={{ padding: "10px 16px 4px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Pipeline</Eyebrow>
          <span style={{ fontSize: 11, color: T.muted }}>
            Actions per stage act on the whole cohort in that stage.
          </span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Stage</th>
              <th style={{ ...th, textAlign: "right" }}>Applications</th>
              <th style={th}>Bar</th>
              <th style={{ ...th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {BOARDING.applications.map((a) => {
              const escalated = escalatedStages.includes(a.stage);
              return (
                <tr key={a.stage}>
                  <td style={{ ...td, fontFamily: sans, fontWeight: 600, color: T.ink }}>
                    {a.stage}
                    {escalated && <> <Pill tone="accent">escalated</Pill></>}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>{f0(a.count)}</td>
                  <td style={{ ...td, width: "40%" }}>
                    <div style={{ height: 8, background: T.paper, borderRadius: 4, overflow: "hidden" }}>
                      <div style={{
                        width: `${(a.count / totalInFlight) * 100}%`, height: "100%", background: T.accent,
                      }} />
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <Btn disabled={escalated} onClick={() => onEscalateStage(a.stage)}>
                      {escalated ? "Escalated" : "Escalate"}
                    </Btn>{" "}
                    <Btn kind="danger" onClick={() => onCancelStageApp(a.stage)} title="Cancel one application in this stage">
                      Cancel one
                    </Btn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {BOARDING.bottleneck && (
        <div style={{
          padding: "10px 16px", background: T.warnSoft, borderTop: `1px solid ${T.line}`,
          fontSize: 12, color: T.body, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        }}>
          <FaExclamationTriangle style={{ color: T.warn, marginTop: 2, flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 240 }}><b>Bottleneck:</b> {BOARDING.bottleneck}</span>
          <Btn kind="primary"
            disabled={escalatedStages.includes("MATCH check")}
            onClick={() => onEscalateStage("MATCH check")}>
            {escalatedStages.includes("MATCH check") ? "Escalated" : "Escalate MATCH review"}
          </Btn>
        </div>
      )}
    </Card>
  );
};

// Audience toggle — Merchant pitch view vs ISO ops view.
const AudienceToggle = ({ audience, onChange }) => (
  <div style={{
    display: "inline-flex", background: T.card, border: `1px solid ${T.line}`,
    borderRadius: 6, padding: 3, gap: 2,
  }}>
    {[["merchant", "Merchant pitch"], ["iso", "ISO ops"]].map(([k, label]) => {
      const on = audience === k;
      return (
        <button key={k} type="button" onClick={() => onChange(k)}
          style={{
            fontFamily: sans, fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 4,
            border: "none", cursor: "pointer",
            background: on ? T.ink : "transparent", color: on ? "#FFF" : T.body,
          }}>
          {label}
        </button>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
const ISOPortfolioDashboard = () => {
  const [vertical, setVertical] = useState("igaming");
  const [audience, setAudience] = useState("iso");         // default to ISO ops (ticket 9)
  const [selected, setSelected] = useState("coastline");   // open on the save story
  const [prospect, setProspect] = useState({
    approvedVolume: 4_000_000, avgTicket: 120, approvalPct: 68.6, cbPerMonth: 78,
    liftPP: 3.5, holdRatePct: VERTICALS.igaming.holdDefault, frictionPct: 18,
    cbReductionPct: 27, cbFee: 22,
  });
  const [threshold, setThreshold] = useState(700);
  const [committed, setCommitted] = useState(700);
  const [agentState, setAgentState] = useState({});
  const [closedCases, setClosedCases] = useState([]);
  const [ledger, setLedger] = useState([]);

  // Ticket 4 remaining sub-actions
  const [threeDsCommitted, setThreeDsCommitted] = useState({}); // { [merchantId]: segmentKey }
  const [kybMerchantPending, setKybMerchantPending] = useState([]); // per-merchant ids
  const [dispositionCase, setDispositionCase] = useState(null); // case row when dialog open

  // ISO ops action state (Ticket 9 continued: application layer for ISO)
  const [sponsorNotified, setSponsorNotified] = useState(false);
  const [velocityTightened, setVelocityTightened] = useState(false);
  const [kybPending, setKybPending] = useState([]);          // merchant ids
  const [escalatedStages, setEscalatedStages] = useState([]);
  const [cancelledApps, setCancelledApps] = useState([]);   // stage names cancelled
  const [boardingPaused, setBoardingPaused] = useState(false);
  const [underwritingTightened, setUnderwritingTightened] = useState(false);

  const m = useMemo(() => MERCHANTS.find((x) => x.id === selected), [selected]);
  const pf = useMemo(() => portfolio(MERCHANTS), []);
  const iso = useMemo(() => isoMetrics(MERCHANTS), []);
  const openCount = pf.openCases - closedCases.length;

  const stamp = () => new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const log = (title, detail, kind) =>
    setLedger((l) => [{ title, detail, kind, at: stamp(), merchant: m.name, by: "demo.rep" }, ...l]);
  // Variant for portfolio-level ISO actions (no single merchant scope).
  const logIso = (title, detail, kind, scope = "Portfolio") =>
    setLedger((l) => [{ title, detail, kind, at: stamp(), merchant: scope, by: "iso.ops" }, ...l]);

  const decideAgent = (verdict) => {
    setAgentState((s) => ({ ...s, [m.id]: { decision: verdict } }));
    if (verdict === "approved") {
      log(`Approved ${m.agent.id}`,
        `${m.agent.proposal}. Projected ${m.agent.projected.vampBps} bps VAMP, ${f1(m.agent.projected.approvalPP)} pp approval. Rule weight retuned.`,
        "projected");
    } else {
      log(`Rejected ${m.agent.id}`,
        "Reason recorded and fed to the retune. Agent lowers confidence on this pattern class.",
        "rejected");
    }
  };

  const commitThreshold = (v) => {
    setCommitted(v);
    log("Committed block threshold", `Moved from ${committed} to ${v} in sandbox, pending approval workflow.`, "projected");
  };

  // Open the disposition dialog instead of instant-closing. Reason and
  // attributor capture is what makes this an examiner-defensible workflow.
  const openDisposition = (c) => setDispositionCase(c);
  const confirmDisposition = ({ reason, note, attributor }) => {
    const c = dispositionCase;
    setClosedCases((l) => [...l, c.id]);
    setDispositionCase(null);
    log(`Dispositioned ${c.id}`,
      `${c.sev}: ${c.title}. Reason: ${reason}. Attributor: ${attributor}.${note ? " Note: " + note : ""} Portfolio open cases now ${openCount - 1}.`,
      "realised");
  };

  // Decision-record export: builds an examiner-defensible JSON artifact
  // and triggers a real browser download. The rep can hand this to the
  // prospect on the spot.
  const exportRecord = (c) => {
    const record = {
      schema_version: "1.0",
      case_id: c.id,
      merchant: { id: m.id, name: m.name, segment: m.segment, live_since: m.liveSince },
      case: { severity: c.sev, title: c.title, opened_age: c.age },
      merchant_snapshot: {
        approval_pct: +approvalPct(m).toFixed(3),
        vamp_bps: +vampBps(m).toFixed(1),
        block_rate_pct: +blockRatePct(m).toFixed(3),
        open_cases_count: openCases(m),
        volume_usd: volumeUsd(m),
      },
      agent_context: m.agent ? {
        agent_id: m.agent.id,
        finding: m.agent.finding,
        proposal: m.agent.proposal,
        confidence: m.agent.confidence,
        evidence_chain: m.agent.evidence,
        projected: m.agent.projected,
      } : null,
      approver: { identity: "demo.rep", role: "iso.ops", tenant: "demo" },
      exported_at: new Date().toISOString(),
      signature: `sha256:${btoa(`${c.id}-${m.id}-${Date.now()}`).slice(0, 44)}=`,
      note: "Demo tenant export. In production this artifact is signed with the approver's key and archived to WORM storage per sponsor policy.",
    };
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `decision-record_${c.id}_${m.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    log(`Exported decision record ${c.id}`,
      `Downloaded decision-record_${c.id}_${m.id}.json — inputs, rule versions, evidence chain, and approver identity, signed. This is the artifact an examiner asks for.`,
      "realised");
  };

  // 3DS step-up on segment — merchant-scoped
  const commitThreeDs = (seg, projected) => {
    setThreeDsCommitted((prev) => ({ ...prev, [m.id]: seg.key }));
    log(`Force 3DS on segment: ${seg.label}`,
      `Projected: ${f0(projected.challengeAdds)} additional challenges, friction cost ${usd(-projected.frictionUsd)}, VAMP relief ${projected.vampReliefBps} bps within two scoring windows.`,
      "projected");
  };

  // Per-merchant KYB refresh — drilldown scope
  const refreshMerchantKyb = (mm) => {
    if (kybMerchantPending.includes(mm.id)) return;
    setKybMerchantPending((prev) => [...prev, mm.id]);
    log(`Requested KYB refresh — ${mm.name}`,
      "Mesh + TheKYB queued in parallel. Results back within 24–48h. Adverse-media hits will auto-open a case.",
      "projected");
  };

  const reset = () => {
    setLedger([]); setClosedCases([]); setAgentState({});
    setThreshold(700); setCommitted(700);
    setSponsorNotified(false); setVelocityTightened(false); setKybPending([]);
    setEscalatedStages([]); setCancelledApps([]); setBoardingPaused(false);
    setUnderwritingTightened(false);
    setThreeDsCommitted({}); setKybMerchantPending([]); setDispositionCase(null);
  };

  // ─── ISO watchlist handlers ────────────────────────────────────────────
  const notifySponsor = () => {
    if (sponsorNotified) return;
    setSponsorNotified(true);
    logIso("Notified sponsor bank of at-risk book",
      `${iso.atRiskCount} merchant${iso.atRiskCount === 1 ? "" : "s"}, ${usd(iso.atRiskResidual)} of residual exposure. Auto-generated compliance packet attached, ack expected within 24h.`,
      "realised");
  };

  const tightenVelocityWatchlist = () => {
    if (velocityTightened) return;
    setVelocityTightened(true);
    const names = iso.trending.map((x) => x.m.name).join(", ");
    logIso("Tightened velocity across watchlist",
      `Applied to ${iso.trending.length} merchants (${names}). Projected VAMP relief 12–18 bps within two scoring windows, approval −0.3 pp average.`,
      "projected");
  };

  const bulkKybRefresh = () => {
    const ids = iso.trending.map((x) => x.m.id).filter((id) => !kybPending.includes(id));
    if (ids.length === 0) return;
    setKybPending((prev) => [...prev, ...ids]);
    logIso("Requested KYB refresh across watchlist",
      `${ids.length} merchant${ids.length === 1 ? "" : "s"} queued through Mesh + TheKYB. Results back 24–48h; overdue flags clear on pass.`,
      "projected");
  };

  const escalateMerchant = (mm) => {
    logIso(`Escalated ${mm.name} to Idenza ops`,
      "Ticket opened, Idenza risk lead paged. Expected ack within 1h during business, 4h off-hours.",
      "realised", mm.name);
  };

  // ─── Boarding-panel handlers ───────────────────────────────────────────
  const escalateStage = (stage) => {
    if (escalatedStages.includes(stage)) return;
    setEscalatedStages((prev) => [...prev, stage]);
    logIso(`Escalated ${stage} queue`,
      `${stage} pipeline flagged for expedite review. Idenza ops paged. Applications in this stage move to the front of the underwriter queue.`,
      "realised", "Boarding");
  };

  const cancelStageApp = (stage) => {
    setCancelledApps((prev) => [...prev, `${stage}-${Date.now()}`]);
    logIso(`Cancelled application in ${stage}`,
      `Application withdrawn. Reason: lead-quality flag. Applicant notified, refund of intake fee queued.`,
      "realised", "Boarding");
  };

  const togglePauseBoarding = () => {
    const willPause = !boardingPaused;
    setBoardingPaused(willPause);
    logIso(willPause ? "Paused new applications" : "Resumed new applications",
      willPause
        ? "Safety switch: no new applications enter the pipeline. Existing applications continue processing."
        : "Applications accepted again from source-of-record.",
      "realised", "Boarding");
  };

  const toggleTightenUnderwriting = () => {
    const willTighten = !underwritingTightened;
    setUnderwritingTightened(willTighten);
    logIso(willTighten ? `Raised underwriting bar for ${VERTICALS[vertical].label}` : "Reverted underwriting bar",
      willTighten
        ? "Higher business-credit threshold + longer sanctions look-back. Applies to new intake only, retroactive review NOT triggered."
        : "Underwriting policy back to standard for this vertical."
      , "realised", "Boarding");
  };

  const onVertical = (k) => {
    setVertical(k);
    setProspect((p) => ({ ...p, holdRatePct: VERTICALS[k].holdDefault }));
  };

  const v = VERTICALS[vertical];

  return (
    <div style={{
      minHeight: "100vh", background: T.paper, padding: "16px clamp(12px, 3vw, 28px) 48px",
      fontFamily: sans, color: T.body,
    }}>
      <DemoBanner />

      {/* command bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-end",
        gap: 16, marginBottom: 14, flexWrap: "wrap",
      }}>
        <div>
          <Eyebrow>Idenza · demo tenant</Eyebrow>
          <h1 style={{
            fontSize: "clamp(20px, 3vw, 27px)", fontWeight: 600, color: T.ink, margin: "6px 0 0",
            letterSpacing: "-0.025em",
          }}>
            {v.label} portfolio
          </h1>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>
            {v.mccNote} · {MERCHANTS.length} merchants · {usd(pf.volume)} monthly volume ·{" "}
            {pf.atRisk} above the standard threshold
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <AudienceToggle audience={audience} onChange={setAudience} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(VERTICALS).map(([k, cfg]) => (
              <Btn key={k} kind={k === vertical ? "primary" : "ghost"} onClick={() => onVertical(k)}>
                {cfg.label.split(" ").slice(0, 2).join(" ")}
              </Btn>
            ))}
          </div>
        </div>
      </div>

      {/* Top-of-page swap: merchant pitch (prospect math) vs ISO ops
          (residuals, watchlist, boarding). Rest of the page — portfolio
          table, drilldown, agent action, rule what-if, ledger — is the
          same either way. */}
      {audience === "merchant" ? (
        <ProspectMath p={prospect} set={setProspect} vertical={vertical} />
      ) : (
        <>
          <IsoTopStrip iso={iso} />
          <IsoWatchlist
            trending={iso.trending}
            onOpen={setSelected}
            onNotifySponsor={notifySponsor}
            onTightenVelocity={tightenVelocityWatchlist}
            onBulkKyb={bulkKybRefresh}
            onEscalate={escalateMerchant}
            sponsorNotified={sponsorNotified}
            velocityTightened={velocityTightened}
            kybPending={kybPending}
          />
          <IsoBoardingPanel
            onEscalateStage={escalateStage}
            onCancelStageApp={cancelStageApp}
            onTogglePause={togglePauseBoarding}
            onToggleTighten={toggleTightenUnderwriting}
            isPaused={boardingPaused}
            isTightened={underwritingTightened}
            escalatedStages={escalatedStages}
            cancelledApps={cancelledApps}
          />
        </>
      )}

      {/* portfolio strip, all derived from MERCHANTS */}
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
          <div style={{ borderRight: `1px solid ${T.line}` }}>
            <Stat label="Approval rate" value={pct(pf.approvalPct)} note={`${f0(pf.approved)} of ${f0(pf.attempted)}`} />
          </div>
          <div style={{ borderRight: `1px solid ${T.line}` }}>
            <Stat label="Portfolio VAMP" value={bps(pf.vampBps)} note="volume weighted"
              tone={postureOf(pf.vampBps) === "ok" ? undefined : "warn"} />
          </div>
          <div style={{ borderRight: `1px solid ${T.line}` }}>
            <Stat label="Block rate" value={pct(pf.blockRatePct)} note="not 'fraud caught', it is a block rate" />
          </div>
          <div style={{ borderRight: `1px solid ${T.line}` }}>
            <Stat label="Merchants at risk" value={`${pf.atRisk} of ${MERCHANTS.length}`}
              note="above standard or worse" tone={pf.atRisk ? "warn" : "good"} />
          </div>
          <div>
            <Stat label="Open cases" value={f0(openCount)} note="sum of the rows below" />
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${T.line}` }}>
          <PortfolioTable list={MERCHANTS} selected={selected} onSelect={setSelected} />
        </div>
      </Card>

      {/* drilldown + ledger */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 14 }}>
        <div style={{
          display: "grid", gap: 14,
          gridTemplateColumns: "minmax(0, 1.85fr) minmax(280px, 1fr)",
        }}>
          <div style={{ minWidth: 0 }}>
            <Card style={{ marginBottom: 14 }}>
              <Head
                title={m.name}
                sub={`${m.segment} · live ${m.liveSince} · ${usd(volumeUsd(m))} monthly · ${pct(approvalPct(m))} approval · ${pct(frictionlessPct(m))} of customers never see a challenge`}
                right={<Pill tone={postureOf(vampBps(m))}>{m.archetype}</Pill>}
              />
              <PostureBar current={vampBps(m)} onAct={() => {
                const el = document.getElementById("agent-action");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }} />
            </Card>

            <div id="agent-action">
              <AgentAction m={m} state={agentState[m.id] || {}} onDecide={decideAgent} />
            </div>

            <RuleWhatIf m={m} threshold={threshold} setThreshold={setThreshold}
              committed={committed} onCommit={commitThreshold} />

            <ThreeDsStepUp
              m={m}
              state={{ committed: threeDsCommitted[m.id] }}
              onCommit={commitThreeDs}
            />

            <MerchantKybRefresh
              m={m}
              pending={kybMerchantPending.includes(m.id)}
              onRefresh={refreshMerchantKyb}
            />

            <TwoUpCharts m={m} />

            <DeclineRecovery m={m} />

            <CaseQueue
              cases={m.cases}
              closed={closedCases}
              onOpenDisposition={openDisposition}
              onExport={exportRecord}
            />

            <Card style={{ marginBottom: 14 }}>
              <Head title={`Built for ${v.label}`} sub="Vertical controls, swapped with the selector above rather than hardcoded into the layout." />
              <div style={{ padding: "12px 16px", display: "grid", gap: 8 }}>
                {v.extras.map((x) => (
                  <div key={x} style={{ display: "flex", gap: 9, fontSize: 12.5, color: T.body, alignItems: "flex-start" }}>
                    <FaCheck size={10} style={{ color: T.good, marginTop: 3, flexShrink: 0 }} />
                    <span>{x}</span>
                  </div>
                ))}
              </div>
            </Card>

            <ProofBand />
          </div>

          <div style={{ minWidth: 0 }}>
            <Ledger entries={ledger} onReset={reset} />
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: T.faint, marginTop: 8, lineHeight: 1.6, maxWidth: 760 }}>
        Close with the pilot, not the page: 90 days in shadow on their own traffic, their
        processor stays live, and the lift number that decides the deal is measured on their
        book. Never describe anything above as production or current.
      </div>

      <DispositionDialog
        open={!!dispositionCase}
        caseRow={dispositionCase}
        onCancel={() => setDispositionCase(null)}
        onConfirm={confirmDisposition}
      />
    </div>
  );
};

export default ISOPortfolioDashboard;
